import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import addHeaders from "../../utils/addHeader";
import ConnectControl from "./ConnectControl";
import useApi from '../../hooks/useApi';
import { useQuery } from '@tanstack/react-query'
import generateSetValueObject, { resetSetValueObject } from "./generateSetValueObject";
import { qk } from "../../utils/queryKeys";
import useTranslation from "../../hooks/useTranslation";
import buildQueryString from "../../utils/api/buildQueryString";

const SelectContainer = styled.div`
    width: 100%;
    margin: 0 auto;
    position: relative;
`

const DualConnect = ({
    mainEntity,
    subEntity,
    mainValueName,
    mainValueId,
    subValueId,
    subValueName,
    mainValueMap,
    // Values written for a mapped field the chosen record leaves empty — without
    // them the previous selection's value would survive (see Connect.jsx).
    mainNullDefaults = {},
    subValueMap,
    setMainValue,
    setSubValue,
    options,
    isRed,
    mainModalTitle,
    subModalTitle,
    mainPlaceholder,
    subPlaceholder,
    showSubField = true,
    menuItemField,
    // What a row in each picker's list reads. `menuItemField` is one field name
    // for both pickers, which is only ever right when the two entities happen to
    // name themselves the same way — a bomVersion has a `version`, not a `name`,
    // so its rows fell back to the raw id. `listDisplay` is the same key the
    // single `connect` already takes, one per picker, and it also makes the
    // list searchable by that field.
    mainListDisplay,
    subListDisplay,
    modalType = 'searchList',
    persistMainValue,
    persistSubValue,
    testId,
    mainEntityParams,
    labelId,
    inputId,
}) => {
    const [mainEntityId, setMainEntityId] = useState(mainValueId || null);
    const [subEntityId, setSubEntityId] = useState(subValueId || null);
    
    const prevMainEntityId = useRef();
    const mainValueMapRef = useRef(mainValueMap);
    const subValueMapRef = useRef(subValueMap);

    const { getMe } = useApi();
    const { t } = useTranslation();

    const mainEntityQs = mainEntityParams ? buildQueryString(mainEntityParams, {}) : '';

    const getMainData = async () => {
        const resource = `${mainEntity}${mainEntityQs}`;
        const response = await getMe(resource);
        return addHeaders(response.data || [{ name: t('no.results.found'), unselectable:true}], options?.withHeaders);
    };

    const getSubData = async () => {
        const resource = `${mainEntity}/${mainEntityId}/${subEntity}`;
        const response = await getMe(resource);
        return addHeaders(response.data || [{ name: t('no.results.found'), unselectable:true }], options?.withHeaders);
    };

    const mainResult = useQuery({
        queryKey: qk.dualMain(mainEntity, mainEntityQs),
        queryFn: getMainData,
        keepPreviousData: true
    });
    const subResult = useQuery({
        queryKey: qk.dualSub(subEntity, mainEntityId),
        queryFn: getSubData,
        keepPreviousData: true,
        enabled: !!mainEntityId
    });

    useEffect(() => {
        // Only proceed if mainEntityId has a value and has changed from the previous non-undefined value
        if (mainEntityId && prevMainEntityId.current !== undefined && mainEntityId !== prevMainEntityId.current) {
            // Clear subentity selection when main entity changes, but exclude fields that exist in mainValueMap
            const subOnlyFields = {};
            for (const key in subValueMapRef.current) {
                if (!Object.hasOwn(mainValueMapRef.current, key)) {
                    subOnlyFields[key] = subValueMapRef.current[key];
                }
            }
            const result = resetSetValueObject(subOnlyFields);
            setSubValue(result);
        }

        // Update the ref with the current mainEntityId for the next effect run
        prevMainEntityId.current = mainEntityId;
    }, [mainEntityId]);


    const handleMainSelection = (data) => {
        const result = generateSetValueObject(data, mainValueMap, mainNullDefaults);
        setMainValue(result);
        //console.log('Setting Main Entity Id:', data.id);
        setMainEntityId(data.id);
    };

    const handleSubSelection = (data) => {
        const result = generateSetValueObject(data, subValueMap);
        setSubValue(result);
        setSubEntityId(data.id);
    };

    const clearMainField = () => {
        setMainValue(resetSetValueObject(mainValueMap));
        setSubValue(resetSetValueObject(subValueMap));
        setMainEntityId(null);
        setSubEntityId(null);
    };

    const clearSubField = () => {
        setSubValue(resetSetValueObject(subValueMap));
        setSubEntityId(null);
    };

    if (mainResult.isError) return <span>{mainResult.error?.message}</span>;

    return (
        <>
            <SelectContainer>
                <ConnectControl
                    valueName={mainValueName}
                    setValue={setMainValue}
                    data={mainResult.data}
                    placeholder={mainPlaceholder}
                    onSelect={handleMainSelection}
                    entity={mainEntity}
                    modalTitle={mainModalTitle}
                    options={options}
                    isRed={isRed}
                    $alwaysOn={true}
                    menuItemField={menuItemField}
                    listDisplay={mainListDisplay}
                    modalType={modalType}
                    persistValue={persistMainValue}
                    testId={testId}
                    id={mainEntityId}
                    onClear={clearMainField}
                    labelId={labelId}
                    inputId={inputId}
                />
            </SelectContainer>
            {(mainEntityId || subValueId) && (showSubField || !!subValueId) && (
                <SelectContainer>
                    {subResult.isError ? (
                        <span>{subResult.error?.message}</span>
                    ) : (subResult.isLoading && !subValueName) ? null
                    : ((subResult.data && subResult.data.list.length > 0) || subValueName) ? (
                        <ConnectControl
                            valueName={subValueName}
                            setValue={setSubValue}
                            data={subResult.data}
                            placeholder={subPlaceholder}
                            onSelect={handleSubSelection}
                            entity={subEntity}
                            modalTitle={subModalTitle}
                            options={options}
                            isRed={false}
                            $alwaysOn={true}
                            menuItemField={menuItemField}
                            listDisplay={subListDisplay}
                            modalType={modalType}
                            persistValue={persistSubValue}
                            testId={`${testId}.${subEntity}`}
                            origin={{entity:mainEntity, id:mainEntityId}}
                            id={subEntityId}
                            onClear={clearSubField}
                        />
                    ) : (
                        <span>{t('no.results.found')}</span>
                    )}
                </SelectContainer>
            )}
        </>
    );
};

export default DualConnect;
